import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
} from '@repo/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  BarChart3,
  Download,
  ExternalLink,
  MessageSquareReply,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import { useCadenceReport, useExportCadenceReport } from '@/hooks/use-cadences'
import type {
  CadenceReport,
  CadenceReportLead,
  CadenceReportLeadSituation,
  CadenceReportRange,
} from '@/types/cadences'

export const Route = createFileRoute('/_app/cadencias/$cadenceId/relatorio')({
  component: CadenceReportPage,
})

const SITUATION_LABEL: Record<CadenceReportLeadSituation, string> = {
  replied: 'Respondeu',
  not_received: 'Não recebeu',
  delivery_failed: 'Falha de entrega',
  read_no_reply: 'Leu e não respondeu',
  delivered_unread: 'Entregue sem leitura',
  sent_unconfirmed: 'Enviado sem confirmação',
}

const NOT_REPLIED_SITUATIONS: CadenceReportLeadSituation[] = [
  'read_no_reply',
  'delivered_unread',
  'sent_unconfirmed',
  'delivery_failed',
  'not_received',
]

type LeadTab = 'replied' | 'not_replied'

function CadenceReportPage() {
  const { cadenceId } = Route.useParams()
  const { currentOrgId } = useAuth()
  const [fromInput, setFromInput] = useState('')
  const [toInput, setToInput] = useState('')
  const [range, setRange] = useState<CadenceReportRange>({})
  const [tab, setTab] = useState<LeadTab>('replied')
  const [situation, setSituation] = useState<
    'all' | CadenceReportLeadSituation
  >('all')

  const {
    data: report,
    isLoading,
    isError,
  } = useCadenceReport(currentOrgId, cadenceId, range)
  const exportCsv = useExportCadenceReport(currentOrgId, cadenceId)

  const timezone = report?.cadence.timezone ?? 'America/Sao_Paulo'

  const leads = useMemo(() => {
    if (!report) return []
    const base =
      tab === 'replied'
        ? report.leads.filter((lead) => lead.repliedAt)
        : report.leads.filter((lead) => !lead.repliedAt)
    const filtered =
      tab === 'not_replied' && situation !== 'all'
        ? base.filter((lead) => lead.situation === situation)
        : base
    return [...filtered].sort((a, b) =>
      tab === 'replied'
        ? (a.repliedAt ?? '').localeCompare(b.repliedAt ?? '')
        : a.situation.localeCompare(b.situation) || a.name.localeCompare(b.name)
    )
  }, [report, tab, situation])

  function applyRange() {
    // Inputs de data são no fuso do navegador; o backend filtra por data de
    // inscrição, então "até" é inclusivo (vira o início do dia seguinte).
    const from = fromInput ? new Date(`${fromInput}T00:00:00`) : null
    const to = toInput ? new Date(`${toInput}T00:00:00`) : null
    if (from && to && from > to) {
      toast.error('A data inicial precisa ser antes da final')
      return
    }
    if (to) to.setDate(to.getDate() + 1)
    setRange({
      from: from?.toISOString(),
      to: to?.toISOString(),
    })
  }

  function clearRange() {
    setFromInput('')
    setToInput('')
    setRange({})
  }

  async function download(group?: 'replied' | 'not_replied') {
    try {
      await exportCsv.mutateAsync({ range, group })
    } catch {
      toast.error('Não foi possível exportar o relatório')
    }
  }

  if (isError) {
    return (
      <p className="text-destructive text-sm">
        Não foi possível carregar o relatório.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      <section className="bg-card border-border flex flex-wrap items-end justify-between gap-4 rounded-xl border p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="report-from" className="text-xs">
              Inscritos de
            </Label>
            <Input
              id="report-from"
              type="date"
              value={fromInput}
              onChange={(event) => setFromInput(event.target.value)}
              className="w-40"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="report-to" className="text-xs">
              até
            </Label>
            <Input
              id="report-to"
              type="date"
              value={toInput}
              onChange={(event) => setToInput(event.target.value)}
              className="w-40"
            />
          </div>
          <Button size="sm" onClick={applyRange}>
            Aplicar
          </Button>
          {range.from || range.to ? (
            <Button size="sm" variant="ghost" onClick={clearRange}>
              Limpar
            </Button>
          ) : null}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              disabled={!report || exportCsv.isPending}
            >
              {exportCsv.isPending ? (
                <Spinner className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Download className="mr-1.5 h-3.5 w-3.5" />
              )}
              Exportar CSV
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void download()}>
              Todos os leads
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void download('replied')}>
              Só quem respondeu
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void download('not_replied')}>
              Só quem não respondeu
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </section>

      {isLoading || !report ? (
        <div className="grid gap-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-20" />
          ))}
        </div>
      ) : (
        <>
          <SummaryCards report={report} timezone={timezone} />

          <div className="grid gap-6 lg:grid-cols-2">
            <section className="bg-card border-border rounded-xl border p-5">
              <h3 className="mb-3 text-sm font-medium">Por etapa</h3>
              <table className="w-full text-sm">
                <thead className="text-muted-foreground text-xs">
                  <tr>
                    <th className="py-1 text-left font-medium">Etapa</th>
                    <th className="py-1 text-left font-medium">Trecho</th>
                    <th className="py-1 text-right font-medium">Enviadas</th>
                    <th className="py-1 text-right font-medium">Falhas</th>
                  </tr>
                </thead>
                <tbody>
                  {report.bySteps.map((step) => (
                    <tr key={step.position} className="border-border border-t">
                      <td className="py-1.5">{step.position + 1}</td>
                      <td className="text-muted-foreground max-w-[220px] truncate py-1.5">
                        {step.excerpt}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {step.sent}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {step.failed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="bg-card border-border rounded-xl border p-5">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-medium">
                <BarChart3 className="text-primary h-4 w-4" />
                Por dia
              </h3>
              {report.byDay.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nenhum disparo no período.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={report.byDay.map((day) => ({
                      ...day,
                      label: formatDay(day.day),
                    }))}
                    margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RechartsTooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="sent"
                      name="Disparos"
                      fill="var(--primary)"
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="replies"
                      name="Respostas"
                      fill="var(--chart-3)"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>
          </div>

          {report.failureReasons.length > 0 ? (
            <section className="bg-card border-border rounded-xl border p-5">
              <h3 className="mb-3 text-sm font-medium">
                Motivos de não ter chegado
              </h3>
              <ul className="space-y-1 text-sm">
                {report.failureReasons.map((item) => (
                  <li
                    key={item.reason}
                    className="flex items-start justify-between gap-4"
                  >
                    <span className="text-muted-foreground">{item.reason}</span>
                    <span className="tabular-nums">{item.leads}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="bg-card border-border rounded-xl border">
            <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
              <div className="flex items-center gap-1">
                <TabButton
                  active={tab === 'replied'}
                  onClick={() => setTab('replied')}
                >
                  Responderam ({report.summary.replied})
                </TabButton>
                <TabButton
                  active={tab === 'not_replied'}
                  onClick={() => setTab('not_replied')}
                >
                  Não responderam (
                  {report.summary.enrolled - report.summary.replied})
                </TabButton>
              </div>
              {tab === 'not_replied' ? (
                <Select
                  value={situation}
                  onValueChange={(value) =>
                    setSituation(value as 'all' | CadenceReportLeadSituation)
                  }
                >
                  <SelectTrigger className="h-8 w-56 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as situações</SelectItem>
                    {NOT_REPLIED_SITUATIONS.map((item) => (
                      <SelectItem key={item} value={item}>
                        {SITUATION_LABEL[item]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
            {leads.length === 0 ? (
              <p className="text-muted-foreground px-5 py-8 text-center text-sm">
                Nenhum lead neste grupo.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-muted-foreground text-xs">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Lead</th>
                      <th className="px-4 py-2 text-left font-medium">
                        Disparo
                      </th>
                      {tab === 'replied' ? (
                        <>
                          <th className="px-4 py-2 text-left font-medium">
                            Respondeu
                          </th>
                          <th className="px-4 py-2 text-right font-medium">
                            Msgs
                          </th>
                          <th className="px-4 py-2 text-right font-medium">
                            Agend.
                          </th>
                          <th className="px-4 py-2 text-left font-medium">
                            Primeira resposta
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-2 text-left font-medium">
                            Situação
                          </th>
                          <th className="px-4 py-2 text-left font-medium">
                            Detalhe
                          </th>
                        </>
                      )}
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <LeadRow
                        key={lead.enrollmentId}
                        lead={lead}
                        tab={tab}
                        timezone={timezone}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}

function SummaryCards({
  report,
  timezone,
}: {
  report: CadenceReport
  timezone: string
}) {
  const { summary } = report
  const period =
    summary.firstSentAt && summary.lastSentAt
      ? `${formatDateTime(summary.firstSentAt, timezone, false)} a ${formatDateTime(summary.lastSentAt, timezone, false)}`
      : 'Nenhum disparo ainda'

  return (
    <section className="space-y-3">
      <div className="grid gap-3 md:grid-cols-4">
        <Tile label="Leads na campanha" value={String(summary.enrolled)} />
        <Tile
          label="Receberam a mensagem"
          value={String(summary.received)}
          hint={`${summary.notReceived} não receberam`}
        />
        <Tile
          label="Entregues no aparelho"
          value={withRate(summary.delivered, summary.deliveredRate)}
          hint={`${summary.failed} falhas de envio`}
        />
        <Tile label="Lidas" value={withRate(summary.read, summary.readRate)} />
        <Tile
          label="Responderam"
          value={withRate(summary.replied, summary.replyRate)}
          hint="% de quem recebeu"
          highlight
        />
        <Tile
          label="Geraram agendamento"
          value={withRate(summary.scheduled, summary.scheduleRate)}
          hint="% de quem respondeu"
        />
        <Tile
          label="Tempo até responder"
          value={
            summary.medianReplyHours === null
              ? '—'
              : `${formatHours(summary.medianReplyHours)} (mediana)`
          }
          hint={
            summary.avgReplyHours === null
              ? undefined
              : `média ${formatHours(summary.avgReplyHours)}`
          }
        />
        <Tile
          label="Mensagens recebidas dos leads"
          value={String(summary.leadMessages)}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Período do disparo: {period} · fuso {timezone}
      </p>
    </section>
  )
}

function Tile({
  label,
  value,
  hint,
  highlight,
}: {
  label: string
  value: string
  hint?: string
  highlight?: boolean
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${highlight ? 'border-primary/40 bg-primary/5' : 'border-border'}`}
    >
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
      {hint ? (
        <p className="text-muted-foreground text-2xs mt-0.5">{hint}</p>
      ) : null}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'bg-muted text-foreground font-medium'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

function LeadRow({
  lead,
  tab,
  timezone,
}: {
  lead: CadenceReportLead
  tab: LeadTab
  timezone: string
}) {
  const conversationId = lead.replyConversationId ?? lead.conversationId
  return (
    <tr className="border-border border-t">
      <td className="px-4 py-2">
        <p className="font-medium">{lead.name}</p>
        <p className="text-muted-foreground text-xs">
          {lead.phone ?? lead.email ?? '—'}
        </p>
      </td>
      <td className="text-muted-foreground px-4 py-2 text-xs whitespace-nowrap">
        {lead.sentAt ? formatDateTime(lead.sentAt, timezone) : '—'}
      </td>
      {tab === 'replied' ? (
        <>
          <td className="px-4 py-2 text-xs whitespace-nowrap">
            {lead.repliedAt ? formatDateTime(lead.repliedAt, timezone) : '—'}
            {lead.replyHours !== null ? (
              <span className="text-muted-foreground">
                {' '}
                · {formatHours(lead.replyHours)}
              </span>
            ) : null}
          </td>
          <td className="px-4 py-2 text-right tabular-nums">
            {lead.leadMessages}
          </td>
          <td className="px-4 py-2 text-right tabular-nums">
            {lead.appointments > 0 ? (
              <Badge
                variant="outline"
                className="border-success/30 text-success-soft-foreground"
              >
                {lead.appointments}
              </Badge>
            ) : (
              '0'
            )}
          </td>
          <td className="text-muted-foreground max-w-[320px] truncate px-4 py-2 text-xs">
            <MessageSquareReply className="mr-1 inline h-3 w-3" />
            {lead.firstReply ?? '—'}
          </td>
        </>
      ) : (
        <>
          <td className="px-4 py-2 text-xs">
            <Badge variant="outline">{SITUATION_LABEL[lead.situation]}</Badge>
          </td>
          <td className="text-muted-foreground max-w-[320px] truncate px-4 py-2 text-xs">
            {lead.failureReason ??
              lead.cadenceError ??
              lead.deliveryStatus ??
              '—'}
          </td>
        </>
      )}
      <td className="px-2 py-2">
        {conversationId ? (
          <Link
            to="/inbox"
            search={{ id: conversationId }}
            className="text-muted-foreground hover:text-foreground inline-flex"
            title="Abrir atendimento"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        ) : null}
      </td>
    </tr>
  )
}

function withRate(count: number, rate: number | null) {
  return rate === null ? String(count) : `${count} (${Math.round(rate * 100)}%)`
}

function formatHours(hours: number) {
  if (hours < 1) return `${Math.round(hours * 60)} min`
  if (hours < 48) return `${hours.toFixed(1).replace('.0', '')} h`
  return `${(hours / 24).toFixed(1).replace('.0', '')} dias`
}

function formatDay(day: string) {
  const [, month, dayOfMonth] = day.split('-')
  return `${dayOfMonth}/${month}`
}

function formatDateTime(iso: string, timezone: string, withTime = true) {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    })
  } catch {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    })
  }
}
